import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertIcon, Avatar, BombIcon, ChartIcon, PhoneIcon, SettingsIcon } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { BASE, fetchReview, instagramProfile, keys, photoUrl } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Pieces the IG Suicide pages share: the frame with its tabs, and an
   account's photo and handle linking to Instagram.
   ────────────────────────────────────────────────────────────────────────── */

export function IgsPage({ intro, actions, children }: { intro?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { can } = useMe();
  // Growth and Ban check are one permission, Accounts another, so a poster can be given only Accounts.
  const growth = can("igsuicide.account:view");
  const accounts = can("igsuicide.stock:view");
  const review = useQuery({ queryKey: keys.review, queryFn: fetchReview, refetchInterval: 120_000, enabled: growth });
  const waiting = review.data?.accounts.length ?? 0;
  const tab = pathname.startsWith("/ig-suicide/review") ? "review" : pathname.startsWith("/ig-suicide/accounts") ? "accounts" : pathname.startsWith("/ig-suicide/setup") ? "setup" : "growth";
  const on = (name: string) => ({ className: tab === name ? "on" : "", "aria-current": tab === name ? ("page" as const) : undefined });
  return (
    <div className="page igs-page">
      <nav className="igs-tabs" aria-label="IG Suicide">
        {/* Module routes are not in the router's typed path list, hence the casts. */}
        {/* First, because IG Suicide opens on it (2026-10-08). */}
        {accounts ? (
          <Link to={"/ig-suicide/accounts" as never} {...on("accounts")}>
            <PhoneIcon size={13} />
            Accounts
          </Link>
        ) : null}
        {growth ? (
          <>
            <Link to={"/ig-suicide" as never} {...on("growth")}>
              <ChartIcon size={13} />
              Growth
            </Link>
            {/* Next to Growth (the boss, 2026-10-22). */}
            <Link to={"/ig-suicide/setup" as never} {...on("setup")}>
              <SettingsIcon size={13} />
              Setup
            </Link>
            <Link to={"/ig-suicide/review" as never} {...on("review")}>
              <AlertIcon size={13} />
              Ban check
              {waiting ? <span className="igs-count">{waiting}</span> : null}
            </Link>
          </>
        ) : null}
      </nav>
      <header className="page-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="igs-title">
            <span className="igs-title-icon"><BombIcon size={20} stroke={1.6} /></span>
            IG Suicide
          </h1>
          {intro ? <p>{intro}</p> : null}
        </div>
        {actions ? <div className="page-head-actions">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}

function InstagramGlyph() {
  return (
    <svg className="igs-ig" width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.3" cy="6.7" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Photo, @handle linking to the profile, and a quiet line under it. */
export function AccountWho({ id, handle, photo, sub, photoSrc }: { id: string; handle: string; photo: boolean; sub?: ReactNode; photoSrc?: string }) {
  return (
    <div className="igs-who" role="cell">
      <Avatar name={handle} src={photo ? photoSrc ?? photoUrl(id) : null} size="small" />
      <div className="table-cell">
        <strong>
          <a href={instagramProfile(handle)} target="_blank" rel="noreferrer noopener" className="igs-handle" title={`Open @${handle} on Instagram`}>
            <span className="truncate">@{handle}</span>
            <InstagramGlyph />
          </a>
        </strong>
        {sub ? <small>{sub}</small> : null}
      </div>
    </div>
  );
}

/** Every creator photo IG Suicide shows, from the one store all of Signet reads. */
export function useIgsCreatorPhotos(): CreatorPhotos {
  return useCreatorPhotos(BASE);
}

/** A creator's name with their photo beside it, as every creator mention on these pages is drawn. */
export function CreatorChip({ name, photos, size = 16 }: { name: string; photos: CreatorPhotos; size?: number }) {
  return (
    <span className="igs-creator">
      <CreatorAvatar name={name} src={photos(name)} size={size} />
      {name.toUpperCase()}
    </span>
  );
}

export function useCanDecide(): boolean {
  return useMe().can("igsuicide.account:edit");
}
